import { $generateNodesFromDOM } from '@lexical/html';
import { convertToRichText } from '@pbk-web/utils';
import type { LexicalEditor } from 'lexical';
import { $createParagraphNode, $createTextNode, $getRoot, $insertNodes } from 'lexical';

import type { EditorMode } from '../types';
import { tableWidthsFromPercent } from './tableWidths';

const parser = new DOMParser();

/**
 * Stored rich values are HTML, but older ones can be markdown; Froala converts those on load, and so do we.
 * - Unlike Froala, HTML is left alone rather than run through `marked` again (it mostly passes through
 *   unchanged, but text like `1.` or `*` inside it could still be reinterpreted).
 */
export const toHtml = (value: string): string => {
  if (value.trimStart().startsWith('<')) return value;
  return convertToRichText(value)?.value ?? '';
};

/**
 * Replaces the whole document with a stored value — the inverse of `$getContent`.
 * - Rich: parses HTML (Froala's or ours, or legacy markdown; see `toHtml`), converting percentage table widths back to Lexical's.
 * - Plain: one paragraph holding the text.
 * - Leaves the root selected so the insert has a target; callers clear it if they mustn't focus.
 */
export const $replaceContent = (editor: LexicalEditor, newContent: string, mode: EditorMode) => {
  const root = $getRoot();
  root.clear();
  root.select();

  if (mode === 'rich') {
    const dom = parser.parseFromString(toHtml(newContent), 'text/html');
    tableWidthsFromPercent(dom);
    const nodes = $generateNodesFromDOM(editor, dom);

    $insertNodes(nodes);
  } else {
    const paragraphNode = $createParagraphNode();
    const textNode = $createTextNode(newContent);
    paragraphNode.append(textNode);

    $insertNodes([paragraphNode]);
  }
};
